import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Mail, Edit, Eye, Send } from 'lucide-react';
import { useState } from 'react';
import EmailPreviewModal from './EmailPreviewModal';
import EmailTemplateTestDialog from './EmailTemplateTestDialog';

interface EmailTemplate {
  id: string;
  template_name: string;
  subject: string;
  body_html: string;
  body_text: string;
  variables: string[];
  is_active: boolean;
  category: string;
}

interface EmailTemplateCardProps {
  template: EmailTemplate;
  onEdit: (template: EmailTemplate) => void;
}

const categoryLabels: Record<string, string> = {
  client_account: 'Client Account',
  documents: 'Documents',
  appointments: 'Appointments',
  tax_filing: 'Tax Filing',
};

const templateLabels: Record<string, string> = {
  new_client_account: 'New Client Account',
  documents_uploaded: 'Documents Uploaded',
  appointment_requested: 'Appointment Requested',
  appointment_approved: 'Appointment Approved',
  day_of_confirmation: 'Day-of Confirmation',
  taxes_filed: 'Taxes Filed',
};

export default function EmailTemplateCard({ template, onEdit }: EmailTemplateCardProps) {
  const [showPreview, setShowPreview] = useState(false);
  const [showTestDialog, setShowTestDialog] = useState(false);

  return (
    <>
      <Card className="hover:border-primary/50 transition-colors">
        <CardHeader>
          <div className="flex items-start justify-between">
            <Mail className="h-5 w-5 text-primary" />
            <Badge variant={template.is_active ? 'default' : 'secondary'}>
              {template.is_active ? 'Active' : 'Inactive'}
            </Badge>
          </div>
          <CardTitle className="text-lg mt-2">
            {templateLabels[template.template_name] || template.template_name}
          </CardTitle>
          <CardDescription>
            {categoryLabels[template.category] || template.category}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="text-sm font-medium mb-1">Subject:</p>
            <p className="text-sm text-muted-foreground">{template.subject}</p>
          </div>
          <div>
            <p className="text-sm font-medium mb-1">Variables:</p>
            <div className="flex flex-wrap gap-1">
              {template.variables.map((variable) => (
                <Badge key={variable} variant="outline" className="text-xs">
                  {variable}
                </Badge>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowPreview(true)}
            >
              <Eye className="h-4 w-4 mr-1" />
              Preview
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setShowTestDialog(true)}
            >
              <Send className="h-4 w-4 mr-1" />
              Test
            </Button>
            <Button
              size="sm"
              onClick={() => onEdit(template)}
            >
              <Edit className="h-4 w-4 mr-1" />
              Edit
            </Button>
          </div>
        </CardContent>
      </Card>

      <EmailPreviewModal
        template={template}
        open={showPreview}
        onOpenChange={setShowPreview}
      />
      
      <EmailTemplateTestDialog
        template={template}
        open={showTestDialog}
        onOpenChange={setShowTestDialog}
      />
    </>
  );
}
